

// src/layout/MainLayout.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink,Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import api from "../utils/api";
import {
  BellIcon,
  SunIcon,
  MoonIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import LogoLight from "../assets/images/Logo.png";
import LogoDark from "../assets/images/onedesk.png";

// Import the new hook
import useOutsideClick from "../hooks/useOutsideClick";   // ← add this

export default function MainLayout({ children, onToggleTheme, mode }) {
  const navigate = useNavigate();
  const isDark = mode === "dark";

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotif, setLoadingNotif] = useState(true);

  // ── Outside click handlers ──────────────────────────────────────────────

  const companyRef = useOutsideClick(() => setShowCompanyMenu(false));
  const notifRef   = useOutsideClick(() => setShowNotifications(false));
  const userRef    = useOutsideClick(() => setShowUserMenu(false));

  // Fetch overdue tasks (unchanged)
  useEffect(() => {
    const fetchOverdueTasks = async () => {
      try {
        setLoadingNotif(true);
        const endpoint = ["SuperAdmin", "Admin", "TeamLead"].includes(user.role)
          ? "/tasks?team=true"
          : "/tasks?mine=true";
        const { data } = await api.get(endpoint);
        const tasks = data.tasks || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const overdue = tasks.filter(task => {
          if (!task.due_date || ["Completed", "Approved"].includes(task.status)) return false;
          return new Date(task.due_date) < today;
        });
        setNotifications(overdue);
      } catch (err) {
        console.error("Notification load failed", err);
        setNotifications([]);
      } finally {
        setLoadingNotif(false);
      }
    };

    fetchOverdueTasks();
    const interval = setInterval(fetchOverdueTasks, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user.role]);

  const notificationCount = notifications.length;

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className={isDark ? "bg-slate-900 text-slate-100" : "bg-gray-50 text-slate-900"}>

      <header className={`sticky top-3 z-40 ${isDark ? "bg-slate-900" : "bg-white"} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="h-16 grid grid-cols-[auto_1fr_auto] items-center">

            {/* LEFT: LOGO + TEXT + TAGLINE */}
            <RouterLink 
              to="/" 
              className="flex items-center gap-3 hover:opacity-90 transition-opacity"
            >
              <img
                src={LogoLight}
                alt="OneDesk Logo (Light)"
                className="h-12 w-auto object-contain block dark:hidden"
              />
              <img
                src={LogoDark}
                alt="OneDesk Logo (Dark)"
                className="h-12 w-auto object-contain hidden dark:block"
              />
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold leading-tight">
                  <span className="text-orange-600">ONE</span> DESK
                </h1>
                <span 
                  className={`text-[10px] font-medium ${
                    isDark ? "text-slate-400" : "text-slate-500"
                  }`}
                >
                  Everything In One Place
                </span>
              </div>
            </RouterLink>

            <div />

            {/* RIGHT SIDE */}
            <div className="flex items-center gap-4">

              {/* COMPANY SWITCHER */}
              <div className="relative" ref={companyRef}>
                <button
                  onClick={() => setShowCompanyMenu(!showCompanyMenu)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium
                    ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}
                  `}
                >
                  Career Mentorz
                  <ChevronDownIcon className="w-4 h-4 inline ml-2" />
                </button>

                {showCompanyMenu && (
                  <div
                    className={`absolute right-0 mt-2 w-56 rounded-xl shadow-lg border z-50
                      ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}
                    `}
                  >
                    <div className="px-4 py-2 font-semibold border-b">
                      Switch Company
                    </div>
                    <button className="w-full text-left px-4 py-2 hover:bg-gray-100">
                      Career Mentorz
                    </button>
                    <button className="w-full text-left px-4 py-2 text-orange-600 hover:bg-orange-50">
                      + Add New Company
                    </button>
                  </div>
                )}
              </div>

              {/* NOTIFICATIONS */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`p-2 rounded-full relative
                    ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}
                  `}
                >
                  <BellIcon className="w-5 h-5 text-orange-600" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notificationCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div
                    className={`absolute right-0 mt-2 w-80 rounded-xl shadow-lg border z-50
                      ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}
                    `}
                  >
                    <div className="px-4 py-3 border-b flex justify-between">
                      <h3 className="font-semibold">Overdue Tasks</h3>
                      <button
                        onClick={() => navigate("/tasks")}
                        className="text-sm text-orange-500"
                      >
                        View All
                      </button>
                    </div>

                    <div className="max-h-80 overflow-y-auto">
                      {loadingNotif ? (
                        <p className="text-center py-6 opacity-60">Loading…</p>
                      ) : notificationCount === 0 ? (
                        <p className="text-center py-6 opacity-60">No overdue tasks 🎉</p>
                      ) : (
                        notifications.map(task => (
                          <div
                            key={task.id}
                            onClick={() => navigate("/tasks")}
                            className="px-4 py-3 cursor-pointer hover:bg-gray-100 border-b last:border-0"
                          >
                            <div className="flex gap-3">
                              <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                              <div>
                                <p className="font-medium">{task.title}</p>
                                <p className="text-sm opacity-70">
                                  Due: {new Date(task.due_date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* THEME TOGGLE */}
              <button
                onClick={onToggleTheme}
                className={`p-2 rounded-full
                  ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}
                `}
              >
                {isDark ? (
                  <SunIcon className="w-5 h-5 text-yellow-400" />
                ) : (
                  <MoonIcon className="w-5 h-5" />
                )}
              </button>

              {/* USER MENU */}
              <div className="relative" ref={userRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center gap-2 px-2 py-2 rounded-full
                    ${isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"}
                  `}
                >
                  <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <ChevronDownIcon className="w-4 h-4 opacity-70" />
                </button>

                {showUserMenu && (
                  <div
                    className={`absolute right-0 mt-2 w-48 rounded-xl shadow-lg border z-50
                      ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}
                    `}
                  >
                    <div className="px-4 py-3 border-b">
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm opacity-70">{user.role}</p>
                    </div>
                    {/* <button className="w-full px-4 py-2 text-left hover:bg-gray-100">
                      My Profile
                    </button> */}
                    <button
                      onClick={logout}
                      className="w-full px-4 py-2 text-left text-red-500 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </header>

      {/* Pill menu bar & main content unchanged */}
      <div className={isDark ? "bg-slate-900" : "bg-white border-b border-gray-200 dark:border-slate-700"}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-center">
          <Sidebar mode={mode} />
        </div>
      </div>
{/* 
      <main className="w-full px-6 py-6">
        {children}
      </main> */}
      <main className="w-full px-6 py-6">
        <Outlet context={{ mode }} />   {/* ← this passes mode to Dashboard */}
      </main>
    </div>
  );
}